export const COMMUNITY_PROJECT_TYPES = [
  'renovation',
  'overseeding',
  'repair',
  'routine-care',
  'striping',
  'new-lawn',
  'other',
] as const

export type CommunityProjectType = (typeof COMMUNITY_PROJECT_TYPES)[number]

export const COMMUNITY_PROJECT_TYPE_LABELS: Record<CommunityProjectType, string> = {
  renovation: 'Full renovation',
  overseeding: 'Overseeding',
  repair: 'Bare spot or damage repair',
  'routine-care': 'Routine care progress',
  striping: 'Mowing or striping',
  'new-lawn': 'New seed or sod',
  other: 'Something else',
}

export const COMMUNITY_PROJECT_TIMEFRAMES = [
  'one-day',
  'under-one-month',
  'one-to-three-months',
  'three-to-six-months',
  'six-to-twelve-months',
  'over-one-year',
  'ongoing',
] as const

export type CommunityProjectTimeframe = (typeof COMMUNITY_PROJECT_TIMEFRAMES)[number]

export const COMMUNITY_PROJECT_TIMEFRAME_LABELS: Record<CommunityProjectTimeframe, string> = {
  'one-day': 'One day',
  'under-one-month': 'Less than a month',
  'one-to-three-months': '1 to 3 months',
  'three-to-six-months': '3 to 6 months',
  'six-to-twelve-months': '6 to 12 months',
  'over-one-year': 'More than a year',
  ongoing: 'Ongoing care',
}

export const COMMUNITY_STORY_LIMITS = {
  startingPoint: { min: 30, max: 400 },
  stepsTaken: { min: 50, max: 800 },
  outcome: { min: 30, max: 400 },
} as const

export interface CommunityStoryFields {
  project_type?: string | null
  photo_taken_month?: string | null
  project_timeframe?: string | null
  starting_point?: string | null
  steps_taken?: string | null
  outcome?: string | null
}

interface CommunitySeoCandidate extends CommunityStoryFields {
  status?: string | null
}

function hasLengthInRange(
  value: string | null | undefined,
  { min, max }: { min: number; max: number },
): boolean {
  if (typeof value !== 'string') return false
  const length = value.trim().length
  return length >= min && length <= max
}

export function isCommunityProjectType(value: unknown): value is CommunityProjectType {
  return typeof value === 'string' &&
    COMMUNITY_PROJECT_TYPES.includes(value as CommunityProjectType)
}

export function isCommunityProjectTimeframe(value: unknown): value is CommunityProjectTimeframe {
  return typeof value === 'string' &&
    COMMUNITY_PROJECT_TIMEFRAMES.includes(value as CommunityProjectTimeframe)
}

/**
 * Convert the composer's privacy-safe YYYY-MM value to the first day of that
 * month for Postgres. Exact capture dates are intentionally never collected.
 */
export function normalizeCommunityPhotoMonth(
  value: unknown,
  now = new Date(),
): string | null {
  if (typeof value !== 'string' || !/^\d{4}-(0[1-9]|1[0-2])$/.test(value)) {
    return null
  }

  const [yearText, monthText] = value.split('-')
  const year = Number(yearText)
  const month = Number(monthText)
  const currentYear = now.getUTCFullYear()
  const currentMonth = now.getUTCMonth() + 1

  if (year < 2000 || year > currentYear) return null
  if (year === currentYear && month > currentMonth) return null

  return `${value}-01`
}

export function isStoredCommunityPhotoMonth(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const match = /^(\d{4})-(0[1-9]|1[0-2])-01$/.exec(value)
  if (!match) return false

  const year = Number(match[1])
  const month = Number(match[2])
  const now = new Date()
  const currentYear = now.getUTCFullYear()
  const currentMonth = now.getUTCMonth() + 1

  return year >= 2000 &&
    year <= currentYear &&
    (year < currentYear || month <= currentMonth)
}

export function getCommunityStoryQualityIssues(
  story: CommunityStoryFields,
): string[] {
  const issues: string[] = []

  if (!isCommunityProjectType(story.project_type)) {
    issues.push('Choose a project type')
  }
  if (!isStoredCommunityPhotoMonth(story.photo_taken_month)) {
    issues.push('Add a valid photo month')
  }
  if (!isCommunityProjectTimeframe(story.project_timeframe)) {
    issues.push('Choose a project timeframe')
  }
  if (!hasLengthInRange(story.starting_point, COMMUNITY_STORY_LIMITS.startingPoint)) {
    issues.push(`Describe the starting point in ${COMMUNITY_STORY_LIMITS.startingPoint.min} to ${COMMUNITY_STORY_LIMITS.startingPoint.max} characters`)
  }
  if (!hasLengthInRange(story.steps_taken, COMMUNITY_STORY_LIMITS.stepsTaken)) {
    issues.push(`Describe the work in ${COMMUNITY_STORY_LIMITS.stepsTaken.min} to ${COMMUNITY_STORY_LIMITS.stepsTaken.max} characters`)
  }
  if (!hasLengthInRange(story.outcome, COMMUNITY_STORY_LIMITS.outcome)) {
    issues.push(`Describe the result in ${COMMUNITY_STORY_LIMITS.outcome.min} to ${COMMUNITY_STORY_LIMITS.outcome.max} characters`)
  }

  return issues
}

export function getCommunitySeoEligibilityIssues(
  photo: CommunitySeoCandidate,
): string[] {
  const issues = getCommunityStoryQualityIssues(photo)
  if (photo.status !== 'approved') {
    issues.unshift('Approve the photo before making it search eligible')
  }
  return issues
}
